package com.schoolconnect.app.repository.itusers

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.AppStaffUpdateRequest
import com.schoolconnect.app.datasource.network.response.ITUsersAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.PlatformUsersResponse
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.repository.common.tenantCacheKey
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.intOrNull

object ITUsersRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ITUsersRepository"

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class ItUsersPageCacheKey(
        val query: String,
        val roleIds: List<Int>,
        val isActive: Boolean?,
        val createdFrom: String,
        val createdTo: String,
        val pageIndex: Int,
        val pageSize: Int,
        val sort: String
    )

    private data class CachedItUsersPage(
        val key: ItUsersPageCacheKey,
        val response: PlatformUsersResponse
    )

    private var cachedItUsersPage: CachedItUsersPage? = null
    private val cachedItUsersAnalytics = mutableMapOf<String, CachedValue<ITUsersAnalyticsResponse>>()

    suspend fun loadItUsersPage(
        query: String = "",
        roleIds: List<Int> = emptyList(),
        isActive: Boolean? = null,
        createdFrom: String = "",
        createdTo: String = "",
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("email", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<PlatformUsersResponse> {
        val normalizedQuery = query.trim()
        val normalizedRoleIds = roleIds.distinct().sorted()
        val normalizedCreatedFrom = createdFrom.trim()
        val normalizedCreatedTo = createdTo.trim()
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceAtLeast(1)
        val cacheKey = ItUsersPageCacheKey(
            query = normalizedQuery,
            roleIds = normalizedRoleIds,
            isActive = isActive,
            createdFrom = normalizedCreatedFrom,
            createdTo = normalizedCreatedTo,
            pageIndex = safePageIndex,
            pageSize = safePageSize,
            sort = sort.cacheKey
        )

        cachedItUsersPage?.takeIf { !forceRemote && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadItUsersPage cacheHit q=${normalizedQuery.ifBlank { "<blank>" }} roles=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize count=${cache.response.users.orEmpty().size}"
            )
            return returnCachedAndRefresh(
                scope = scope,
                tag = TAG,
                operation = "loadItUsersPage",
                context = "pageIndex=$safePageIndex pageSize=$safePageSize",
                cached = cache.response,
                refresh = { loadItUsersPage(query, roleIds, isActive, createdFrom, createdTo, safePageIndex, safePageSize, sort, forceRemote = true) }
            )
        }

        val offset = safePageIndex * safePageSize
        scLog(
            "$TAG loadItUsersPage start q=${normalizedQuery.ifBlank { "<blank>" }} roles=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote"
        )
        return runCatching {
            val response = KtorApiClient.getAppStaff(
                q = normalizedQuery.ifBlank { null },
                roleIds = normalizedRoleIds,
                isActive = isActive,
                limit = safePageSize,
                offset = offset,
                sortBy = sort.normalizedField,
                order = sort.order.apiValue
            ).getOrElse { throw it }

            // Keep this page backed by one API. Role fields remain optional until
            // the app-staff endpoint includes them in its response.
            val deduped = response.copy(
                users = response.users.orEmpty().distinctBy { it.userId ?: it.id.orEmpty() }
            )
            cachedItUsersPage = CachedItUsersPage(cacheKey, deduped)
            scLog(
                "$TAG loadItUsersPage complete q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize total=${deduped.count ?: 0} count=${deduped.users.orEmpty().size}"
            )
            deduped
        }.onFailure { throwable ->
            scLog(
                "$TAG loadItUsersPage failure q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize",
                throwable
            )
        }
    }

    suspend fun loadItUsersAnalytics(
        forceRemote: Boolean = false
    ): Result<ITUsersAnalyticsResponse> {
        val cacheKey = tenantCacheKey("it-users-analytics:platform")
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadItUsersAnalytics",
            context = "forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cachedItUsersAnalytics[cacheKey],
            fetchValue = { KtorApiClient.getAppStaffAnalytics() },
            updateCache = { response, fetchedAt ->
                cachedItUsersAnalytics[cacheKey] = CachedValue(response, fetchedAt)
            },
            successDetails = { response ->
                "scope=${response.scope.ifBlank { "<blank>" }} total=${response.metrics.totalUsers} active=${response.metrics.activeUsers} inactive=${response.metrics.inactiveUsers}"
            }
        )
    }

    fun invalidateCaches() {
        cachedItUsersPage = null
        cachedItUsersAnalytics.clear()
    }

    suspend fun updateAppStaff(
        staffId: String,
        request: AppStaffUpdateRequest
    ): Result<Unit> {
        val normalizedStaffId = staffId.trim()
        scLog("$TAG updateAppStaff start staffId=$normalizedStaffId")
        return runCatching {
            KtorApiClient.updateAppStaff(normalizedStaffId, request).getOrElse { throw it }
            invalidateCaches()
            scLog("$TAG updateAppStaff complete staffId=$normalizedStaffId")
        }
    }

    suspend fun toggleAppStaffStatus(staffId: String, isActive: Boolean): Result<Unit> {
        val normalizedStaffId = staffId.trim()
        scLog("$TAG toggleAppStaffStatus start staffId=$normalizedStaffId isActive=$isActive")
        return runCatching {
            KtorApiClient.toggleAppStaffStatus(normalizedStaffId, isActive).getOrElse { throw it }
            invalidateCaches()
            scLog("$TAG toggleAppStaffStatus complete staffId=$normalizedStaffId isActive=$isActive")
        }
    }

    suspend fun deleteAppStaff(staffId: String): Result<Unit> {
        val normalizedStaffId = staffId.trim()
        scLog("$TAG deleteAppStaff start staffId=$normalizedStaffId")
        return runCatching {
            KtorApiClient.deleteAppStaff(normalizedStaffId).getOrElse { throw it }
            invalidateCaches()
            scLog("$TAG deleteAppStaff complete staffId=$normalizedStaffId")
        }
    }
}
